import { useState } from 'react';
import {
  ActionButton,
  Alert,
  Button,
  CircularProgress,
  DataTable,
  dataTableSx,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  iconSize,
  MenuItem,
  PeriodCell,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  statusTone,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ContractStatus, ContractSummary } from '@munifocus/contracts-gov';
import {
  openContractDocument,
  useCompanies,
  useContracts,
  useCreateContract,
  useRejectContract,
  useResumeContract,
  useSignContract,
  useSubmitContract,
  useSuspendContract,
  useTerminateContract,
  useTerminationPlan,
} from '../../lib/administration-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { formatDate } from '../../lib/relative-time';
import { useConfirmAction } from '../../lib/useConfirmAction';
import { useSubmitValidation } from '../../lib/useSubmitValidation';
import { DeclineDialog } from './DeclineDialog';

/**
 * Status colour. `pending_council` is deliberately not a warning: a proposal
 * awaiting a resolution is the normal state of a contract, not a problem.
 */

/**
 * A contract's period, in one line — the shared `PeriodCell` with this
 * portal's date format and words. No start date means the contract has not
 * been signed: its period begins when council approves it.
 */
function TermCell({ startsOn, endsOn }: { startsOn: string | null; endsOn: string | null }) {
  const { t, i18n } = useTranslation();
  return (
    <PeriodCell
      start={startsOn ? formatDate(startsOn, i18n.language) : null}
      end={endsOn ? formatDate(endsOn, i18n.language) : null}
      noStartLabel={t('gov.admin.contracts.startsOnApproval')}
      noEndLabel={t('gov.admin.contracts.openEnded')}
    />
  );
}

/**
 * Contracts between companies and this municipality — the edge council controls.
 *
 * The two powers are kept visibly apart, because they are apart in law: counsel
 * prepares a contract and sends it to council, and only council makes it live.
 * A person holding both sees both buttons; most people see one.
 */
export function ContractsPanel() {
  const { t } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const toast = useMutationToast();
  const navigate = useNavigate();
  const confirm = useConfirmAction();
  const { active, can } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : undefined;
  const companyId = active?.kind === 'company' ? active.id : undefined;

  const contracts = useContracts({ localGovernmentId, companyId });
  const submit = useSubmitContract();
  const sign = useSignContract();
  const reject = useRejectContract();
  const suspend = useSuspendContract();
  const resume = useResumeContract();
  const terminate = useTerminateContract();

  const [signing, setSigning] = useState<ContractSummary | null>(null);
  const [declining, setDeclining] = useState<ContractSummary | null>(null);
  const [resolution, setResolution] = useState('');
  const vSign = useSubmitValidation({
    resolution: resolution.trim() ? null : t('common.validation.required'),
  });
  const closeSigning = () => {
    setSigning(null);
    vSign.reset();
  };
  const [terminating, setTerminating] = useState<ContractSummary | null>(null);
  const [adding, setAdding] = useState(false);
  const [newCompanyId, setNewCompanyId] = useState('');
  const [newReference, setNewReference] = useState('');

  // Only loaded when council is actually engaging a company — the registry is
  // Munifocus-wide and irrelevant to everyone else on this screen.
  const companies = useCompanies(can('company:appoint'));
  const createContract = useCreateContract();

  const plan = useTerminationPlan(terminating?.id ?? null, terminating?.localGovernmentId ?? '');

  const rows = contracts.data ?? [];

  /**
   * Every write confirms or explains itself. The API's own message is the useful
   * one here — "a council resolution reference is required to sign" beats any
   * generic failure line this screen could invent.
   *
   * Resolves whether the action landed, so a dialog can stay open — with what
   * was typed still in it — when it did not.
   */
  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  /** Companies with no contract here yet — the only ones worth offering. */
  const available = (companies.data ?? []).filter(
    (company) => company.status === 'active' && !rows.some((row) => row.companyId === company.id),
  );

  return (
    <Stack spacing={2}>
      <DataTable
        rows={rows}
        getRowKey={(row) => row.id}
        isLoading={contracts.isLoading}
        emptyLabel={t('gov.admin.contracts.empty')}
        search={{
          placeholder: t('gov.admin.contracts.searchPlaceholder'),
          of: (row) => `${row.companyName} ${row.localGovernmentName} ${row.reference}`,
        }}
        initialSortKey="company"
        pageSize={25}
        action={
          can('company:appoint') && localGovernmentId ? (
            <ActionButton
              label={t('gov.admin.contracts.engage')}
              icon={Icons.Plus}
              onClick={() => setAdding(true)}
            />
          ) : null
        }
        columns={[
          {
            key: 'company',
            label: t('gov.admin.contracts.company'),
            primary: true,
            sortValue: (row) => row.companyName,
            cell: (row) => row.companyName,
          },
          {
            key: 'localGovernment',
            label: t('gov.admin.contracts.localGovernment'),
            sortValue: (row) => row.localGovernmentName,
            cell: (row) => row.localGovernmentName,
          },
          {
            key: 'reference',
            label: t('gov.admin.contracts.reference'),
            mono: true,
            sortValue: (row) => row.reference,
            cell: (row) => row.reference || '—',
          },
          {
            key: 'status',
            label: t('gov.admin.contracts.status'),
            sortValue: (row) => row.status,
            cell: (row) => (
              <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                <StatusChip
                  status={statusTone.contract[row.status]}
                  label={t(`gov.admin.contracts.statuses.${row.status}`)}
                />
                {/* A draft council sent back says why, so the company knows
                    what to change before submitting again. */}
                {row.status === 'draft' && row.rejectionReason && (
                  <Tooltip title={row.rejectionReason}>
                    <Typography variant="caption" color="error" sx={{ maxWidth: 220 }} noWrap>
                      {t('gov.admin.decline.returned', { reason: row.rejectionReason })}
                    </Typography>
                  </Tooltip>
                )}
              </Stack>
            ),
          },
          {
            key: 'term',
            label: t('gov.admin.contracts.term'),
            nowrap: true,
            sortValue: (row) => row.startsOn,
            cell: (row) => <TermCell startsOn={row.startsOn} endsOn={row.endsOn} />,
          },
          {
            key: 'officers',
            label: t('gov.admin.contracts.officers'),
            align: 'right',
            nowrap: true,
            sortValue: (row) => row.appointmentCount,
            cell: (row) => row.appointmentCount,
          },
          {
            // Whether the signed copy is on file, in the list and not only
            // inside the editor — "which of these have we never actually
            // received" is a question the table should answer at a glance.
            key: 'document',
            label: t('gov.admin.contracts.document'),
            nowrap: true,
            sortValue: (row) => (row.documentFilename ? 0 : 1),
            cell: (row) =>
              row.documentFilename ? (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Icons.FileText size={iconSize.sm} />}
                  onClick={() => {
                    void openContractDocument(row.id, row.localGovernmentId).catch((err) =>
                      toast.error(err, 'gov.admin.contracts.documentFailed'),
                    );
                  }}
                >
                  {t('gov.admin.contracts.viewDocument')}
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('gov.admin.contracts.noDocument')}
                </Typography>
              ),
          },
          {
            ...actionsColumn,
            cell: (row) => (
              <RowActions
                // View and Edit come first because reading the contract is what
                // people came here to do; the lifecycle acts that follow are the
                // exception, not the rule. Every status but `terminated` is
                // editable — a live contract whose reference was typed wrong is
                // still wrong, and correcting it is not the same act as
                // re-signing it.
                onView={() => navigate(`/administration/contracts/${row.id}`)}
                onEdit={
                  can('contract:draft') && row.status !== 'terminated'
                    ? () => navigate(`/administration/contracts/${row.id}/edit`)
                    : undefined
                }
              >
                {can('contract:draft') && row.status === 'draft' && (
                  <RowAction
                    label={t('gov.admin.contracts.submit')}
                    icon={Icons.Send}
                    onClick={() =>
                      run('gov.admin.contracts.submitted', () =>
                        submit.mutateAsync({
                          id: row.id,
                          body: { localGovernmentId: row.localGovernmentId },
                        }),
                      )
                    }
                  />
                )}
                {can('contract:sign') && row.status !== 'active' && row.status !== 'terminated' && (
                  <RowAction
                    label={t('gov.admin.contracts.sign')}
                    icon={Icons.FileSignature}
                    onClick={() => {
                      setSigning(row);
                      setResolution(row.councilResolutionRef);
                    }}
                  />
                )}
                {can('contract:sign') && row.status === 'pending_council' && (
                  <RowAction
                    label={t('gov.admin.decline.action')}
                    icon={Icons.X}
                    tone="danger"
                    onClick={() => setDeclining(row)}
                  />
                )}
                {can('contract:terminate') && row.status === 'active' && (
                  <RowAction
                    label={t('gov.admin.contracts.suspend')}
                    icon={Icons.Pause}
                    onClick={async () => {
                      // Only `active` grants, so suspending takes the whole
                      // contracted workforce off this municipality's files
                      // immediately — reversible, but not quietly.
                      if (
                        !(await confirm({
                          title: t('gov.admin.contracts.suspendTitle'),
                          body: t('gov.admin.contracts.suspendBody', {
                            company: row.companyName,
                            count: row.appointmentCount,
                          }),
                          confirmLabel: t('gov.admin.contracts.suspend'),
                          tone: 'danger',
                        }))
                      ) {
                        return;
                      }
                      await run('gov.admin.contracts.suspended', () =>
                        suspend.mutateAsync({
                          id: row.id,
                          body: { localGovernmentId: row.localGovernmentId },
                        }),
                      );
                    }}
                  />
                )}
                {can('contract:sign') && row.status === 'suspended' && (
                  <RowAction
                    label={t('gov.admin.contracts.resume')}
                    icon={Icons.Play}
                    onClick={() =>
                      run('gov.admin.contracts.resumed', () =>
                        resume.mutateAsync({
                          id: row.id,
                          body: { localGovernmentId: row.localGovernmentId },
                        }),
                      )
                    }
                  />
                )}
                {can('contract:terminate') && row.status !== 'terminated' && (
                  <RowAction
                    label={t('gov.admin.contracts.terminate')}
                    icon={Icons.Trash2}
                    tone="danger"
                    onClick={() => setTerminating(row)}
                  />
                )}
              </RowActions>
            ),
          },
        ]}
      />

      {/* Engaging a company creates a *draft*. Signing is a separate act with a
          separate permission, because a contract that exists and a contract that
          binds the municipality are different things. */}
      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.contracts.engage')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('gov.admin.contracts.engageHelp')}</Alert>
            <TextField
              select
              label={t('gov.admin.contracts.company')}
              value={newCompanyId}
              onChange={(e) => setNewCompanyId(e.target.value)}
              fullWidth
              required
              helperText={available.length === 0 ? t('gov.admin.contracts.allEngaged') : undefined}
            >
              {available.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.operatingName || company.legalName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('gov.admin.contracts.reference')}
              value={newReference}
              onChange={(e) => setNewReference(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!newCompanyId || createContract.isPending}
            onClick={async () => {
              if (!localGovernmentId) return;
              const ok = await run('gov.admin.contracts.engaged', () =>
                createContract.mutateAsync({
                  companyId: newCompanyId,
                  localGovernmentId,
                  reference: newReference,
                }),
              );
              if (!ok) return;
              setAdding(false);
              setNewCompanyId('');
              setNewReference('');
            }}
          >
            {t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signing: the resolution reference is required, because a municipality
          is bound by a council resolution and not by whoever is logged in. */}
      <Dialog open={Boolean(signing)} onClose={closeSigning} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.contracts.signTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.admin.contracts.signHelp', { company: signing?.companyName ?? '' })}
            </Typography>
            <TextField
              label={t('gov.admin.contracts.resolution')}
              {...vSign.field('resolution')}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              required
              fullWidth
              autoFocus
              helperText={vSign.errors.resolution ?? t('gov.admin.contracts.resolutionHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSigning}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={sign.isPending}
            onClick={vSign.guard(async () => {
              if (!signing) return;
              const ok = await run('gov.admin.contracts.signed', () =>
                sign.mutateAsync({
                  id: signing.id,
                  body: { councilResolutionRef: resolution.trim() },
                }),
              );
              if (!ok) return;
              closeSigning();
            })}
          >
            {t('gov.admin.contracts.sign')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeclineDialog
        open={Boolean(declining)}
        title={t('gov.admin.contracts.declineTitle')}
        help={t('gov.admin.contracts.declineHelp', { company: declining?.companyName ?? '' })}
        pending={reject.isPending}
        onClose={() => setDeclining(null)}
        onConfirm={(reason) =>
          declining
            ? run('gov.admin.contracts.declined', () =>
                reject.mutateAsync({ id: declining.id, body: { reason } }),
              )
            : Promise.resolve(false)
        }
      />

      {/* Termination: shows what it would do before it does it. Ending a
          contract removes an entire workforce from live files. */}
      <Dialog
        open={Boolean(terminating)}
        onClose={() => setTerminating(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('gov.admin.contracts.terminateTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('gov.admin.contracts.terminateHelp', {
                company: terminating?.companyName ?? '',
              })}
            </Typography>
            {plan.isLoading && <CircularProgress size={20} />}
            {plan.data && plan.data.files.length > 0 && (
              <Alert severity={plan.data.canProceed ? 'warning' : 'error'}>
                {plan.data.canProceed
                  ? t('gov.admin.revocation.willTransfer', { count: plan.data.files.length })
                  : t('gov.admin.revocation.blocked', { count: plan.data.unresolvableCount })}
              </Alert>
            )}
            {plan.data && plan.data.files.length > 0 && (
              // The same header rhythm and row height as every other table in the
              // portal. A dialog is not a reason for a table to look different.
              <Table size="small" sx={dataTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('gov.admin.revocation.file')}</TableCell>
                    <TableCell>{t('gov.admin.revocation.from')}</TableCell>
                    <TableCell>{t('gov.admin.revocation.to')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.data.files.map((file) => (
                    <TableRow key={file.fileId}>
                      <TableCell>{file.fileReference}</TableCell>
                      <TableCell>{file.currentLeadName}</TableCell>
                      <TableCell>
                        {file.proposedSuccessorName ?? (
                          <Typography variant="body2" color="error">
                            {t('gov.admin.revocation.noSuccessor')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminating(null)}>{t('common.actions.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={terminate.isPending || plan.data?.canProceed === false}
            onClick={async () => {
              if (!terminating) return;
              const ok = await run('gov.admin.contracts.terminated', () =>
                terminate.mutateAsync({ id: terminating.id, body: {} }),
              );
              if (!ok) return;
              setTerminating(null);
            }}
          >
            {t('gov.admin.contracts.terminate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
